/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import Button from '@atlaskit/button/new';
import CloudArrowUpIcon from '@atlaskit/icon/core/cloud-arrow-up';
import { Text, Box } from '@atlaskit/primitives/compiled';
import { dropTargetForExternal } from '@atlaskit/pragmatic-drag-and-drop/external/adapter';
import { getFiles } from '@atlaskit/pragmatic-drag-and-drop/external/file';
import { token } from '@atlaskit/tokens';
import { type RenderFn } from '@atlassian/forge-ui-types';
import { useAdaptEventHandler } from '../../utils';

const dropzoneStyle = css({
	borderStyle: 'dashed',
	borderWidth: token('border.width'),
	borderColor: token('color.border.input'),
	borderRadius: token('radius.small'),
	paddingTop: token('space.150'),
	paddingRight: token('space.150'),
	paddingBottom: token('space.150'),
	paddingLeft: token('space.150'),
	textAlign: 'center',
});

const inDropzoneStyle = css({
	borderColor: token('color.border.selected'),
	backgroundColor: token('color.background.accent.blue.subtlest'),
});
const notInDropzoneStyle = css({
	borderColor: token('color.border.input'),
});

type SerializedFile = {
	data: string;
	name: string;
	size: number;
	type: string;
};

export type FilePickerProps = {
	/**
	 * Description of the file picker to be used for file requirements
	 */
	description?: string;
	/**
	 * Label of the file picker
	 */
	label?: string;
	/**
	 * Callback function to be called when the files are changed
	 */
	onChange?: (files: SerializedFile[]) => void | Promise<void>;
	/**
	 * Test ID for testing
	 */
	testId?: string;
};

const serializeFiles = async (files: File[]) => {
	return Promise.all(
		files.map(async (file) => {
			const buffer = await file.arrayBuffer();
			return {
				name: file.name,
				type: file.type,
				size: file.size,
				data: Buffer.from(buffer).toString('base64'),
			};
		}),
	);
};

export const FilePicker = (props: Parameters<RenderFn>[0]): JSX.Element => {
	const { description, label, onChange, testId } = props.forgeDoc.props as FilePickerProps;
	const [inDropzone, setInDropzone] = useState(false);
	const adaptEventHandler = useAdaptEventHandler();

	const inputRef = useRef<HTMLInputElement>(null);
	const dropRef = useRef<HTMLDivElement>(null);

	const onInputTriggerClick = useCallback(() => {
		inputRef.current?.click();
	}, []);

	useEffect(() => {
		const element = dropRef.current;
		if (!element) {
			return;
		}
		return dropTargetForExternal({
			element,
			onDragEnter: () => {
				setInDropzone(true);
			},
			onDragLeave: () => {
				setInDropzone(false);
			},
			onDrop: onChange
				? async ({ source }) => {
						const files = await getFiles({ source });
						const serializedFiles = await serializeFiles(files);
						setInDropzone(false);
						await onChange(serializedFiles);
					}
				: () => {
						setInDropzone(false);
					},
		});
	}, [onChange]);

	return (
		<Box testId={testId}>
			{label && (
				<Text weight="bold" size="small">
					{label}
				</Text>
			)}
			<input
				type="file"
				multiple
				onChange={
					onChange
						? async (e: React.ChangeEvent<HTMLInputElement>) => {
								const files = Array.from(e.target.files || []) as File[];
								const serializedFiles = await serializeFiles(files);
								adaptEventHandler(() => onChange(serializedFiles))?.(e);
							}
						: undefined
				}
				hidden
				ref={inputRef}
			/>
			<div css={[dropzoneStyle, inDropzone ? inDropzoneStyle : notInDropzoneStyle]} ref={dropRef}>
				<Text color={inDropzone ? 'color.text.selected' : 'color.text.subtlest'}>
					<CloudArrowUpIcon
						label="file-picker-icon"
						color={inDropzone ? token('color.icon.selected') : token('color.icon')}
					/>
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					{'  '}
					Drop files to attach or{'  '}
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					<Button onClick={onInputTriggerClick}>Browse</Button>
				</Text>
			</div>
			{description && (
				<Text size="small" color="color.text.subtlest">
					{description}
				</Text>
			)}
		</Box>
	);
};
